import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchInvoiceItems } from "../Api/VendorInvoice";

import { FiSearch } from "react-icons/fi";
import { Package, Hash, IndianRupee, BarChart3 } from "lucide-react";

import "../Styling/PayrollTheme.css";
import "../Styling/VIMTheme.css";

const formatCurrency = (value) => {
  if (value === null || value === undefined) return "—";

  return Number(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const VIMInvoiceItems = () => {
  const { guid } = useParams();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [hsmFilter, setHsmFilter] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");

  useEffect(() => {
    fetchInvoiceItems(guid)
      .then(setItems)
      .finally(() => setLoading(false));
  }, [guid]);

  const filteredItems = items.filter((item) => {
    const matchesSearch =
      !searchTerm ||
      item?.Description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesHsm = !hsmFilter || item?.HSMCode === hsmFilter;

    const amount = Number(item?.Amount || 0);

    const matchesMin = !minAmount || amount >= Number(minAmount);

    const matchesMax = !maxAmount || amount <= Number(maxAmount);

    return matchesSearch && matchesHsm && matchesMin && matchesMax;
  });

  const totalItems = items.length;

  const totalQuantity = items.reduce(
    (sum, item) => sum + Number(item.Quantity || 0),
    0,
  );

  const totalAmount = items.reduce(
    (sum, item) => sum + Number(item.Amount || 0),
    0,
  );

  const averageUnitPrice =
    totalItems > 0
      ? items.reduce((sum, item) => sum + Number(item.UnitPrice || 0), 0) /
        totalItems
      : 0;

  if (loading) {
    return (
      <div className="vim-page__loading">
        <span className="vim-page__loading-dot" />
        <span className="vim-page__loading-dot" />
        <span className="vim-page__loading-dot" />
      </div>
    );
  }

  return (
    <div className="vim-page">
      {/* Header */}
      <div className="vim-page__header">
        <h2 className="vim-page__title">Invoice Item Details</h2>

        <p className="vim-page__subtitle">
          Item details associated with selected invoice
        </p>
      </div>

      {/* Summary Label */}
      <div
        style={{
          padding: "14px 24px 0 24px",
          fontSize: "10px",
          fontWeight: 700,
          color: "#9ca3af",
          textTransform: "uppercase",
          letterSpacing: "0.07em",
          background: "#ffffff",
        }}
      >
        Item Summary
      </div>

      {/* Summary Cards */}
      <div className="resource-dashboard-summary">
        <div className="resource-card highlight">
          <div className="card-row">
            <Package className="card-icon blue" />
            <div className="card-text">
              <p>Total Items</p>
              <h3>{totalItems}</h3>
            </div>
          </div>
        </div>

        <div className="resource-card accent">
          <div className="card-row">
            <Hash className="card-icon orange" />
            <div className="card-text">
              <p>Total Quantity</p>
              <h3>{totalQuantity}</h3>
            </div>
          </div>
        </div>

        <div className="resource-card">
          <div className="card-row">
            <IndianRupee className="card-icon purple" />
            <div className="card-text">
              <p>Total Amount</p>
              <h3>₹ {formatCurrency(totalAmount)}</h3>
            </div>
          </div>
        </div>

        <div className="resource-card">
          <div className="card-row">
            <BarChart3 className="card-icon gray" />
            <div className="card-text">
              <p>Avg Unit Price</p>
              <h3>₹ {formatCurrency(averageUnitPrice)}</h3>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="vim-filter-bar">
        <div className="vim-search-box">
          <FiSearch className="vim-search-icon" />

          <input
            type="text"
            placeholder="Search description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select
          value={hsmFilter}
          onChange={(e) => setHsmFilter(e.target.value)}
        >
          <option value="">All HSN Codes</option>

          {[...new Set(items.map((x) => x.HSMCode))]
            .filter(Boolean)
            .map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
        </select>

        <button
          className="clear-filter-btn"
          onClick={() => {
            setSearchTerm("");
            setHsmFilter("");
            setMinAmount("");
            setMaxAmount("");
          }}
        >
          Clear
        </button>
      </div>

      {/* Table */}
      <div className="vim-grid">
        <div className="vim-grid__card">
          <div className="vim-grid__scroll">
            <table className="vim-grid__table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Invoice Number</th>

                  <th>HSN Code</th>
                  <th>Quantity</th>
                  <th>Unit Price</th>
                  <th>Amount</th>
                </tr>
              </thead>

              <tbody>
                {filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="vim-grid__empty">
                      No items found
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((item, index) => (
                    <tr key={index}>
                      <td>{item.Description || "—"}</td>
                      <td className="vim-link">{item.InvoiceNumber || "—"}</td>

                      <td>{item.HSMCode || "—"}</td>

                      <td>{item.Quantity ?? "—"}</td>

                      <td>{formatCurrency(item.UnitPrice)}</td>

                      <td>{formatCurrency(item.Amount)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VIMInvoiceItems;
